// ===== AUTO FREE KEY SYSTEM =====
// v2 - one key product, two variants
//   35* SKU = paid / orderable variant (visible on storefront)
//   53* SKU = free variant (EUR 0), auto-assigned with aerators -> goes to OPACC at 0
//
// Behaviour:
//   - Aerator in cart        -> adds the 53* variant (qty 1, _freeKey: true)
//   - Free line qty > 1      -> free line reset to 1, extra qty added as the 35* variant
//   - 53* added without flag -> converted to the 35* variant (no free keys via direct add)
//   - Last aerator removed   -> free line removed
//   - Customer removes key   -> not re-added until the aerator mix changes

(function() {
    'use strict';

    // --- Config ---
    const FREE_PREFIX = '53';
    const PAID_PREFIX = '35';
    const FREE_PROP = '_freeKey';
    const API_DELAY = 1000;
    const PRODUCTS_PAGE_LIMIT = 250;
    const PRODUCTS_MAX_PAGES = 20;

    const PRODUCT_KEY_MAPPING = {}; // aerator SKU -> key SKU (from ?view=free-key-meta; 35* or 53* both work)
    const KEY_PAIRS = {};           // any key variant SKU -> { product, free, paid } (null = not a key product)
    let keyProductCache = {};
    let productCacheLoaded = false;
    let productCachePromise = null;
    let isProcessing = false;
    let lastApiCall = 0;

    // Track manually removed keys (by free SKU) and cart composition
    let manuallyRemovedKeys = new Set();
    let lastRequiredProducts = new Set();

    // ---------- Helpers ----------

    function isFlaggedFree(item) {
        return !!(item.properties && item.properties[FREE_PROP] === 'true');
    }

    function skuStarts(sku, prefix) {
        return typeof sku === 'string' && sku.startsWith(prefix);
    }

    // Rate limiting - only for Shopify API endpoints
    async function waitForRateLimit() {
        const now = Date.now();
        const timeSinceLastCall = now - lastApiCall;
        if (timeSinceLastCall < API_DELAY) {
            await new Promise(resolve => setTimeout(resolve, API_DELAY - timeSinceLastCall));
        }
        lastApiCall = Date.now();
    }

    // Safe API calls - rate limited (for Shopify API endpoints only)
    async function safeApiCall(url, options = {}) {
        await waitForRateLimit();
        const response = await fetch(url, options);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    }

    // Get cart - no rate limit needed
    async function getCart() {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    }

    async function addToCart(variantId, quantity, properties) {
        const item = { id: variantId, quantity };
        if (properties) item.properties = properties;
        return safeApiCall('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [item] })
        });
    }

    async function changeLine(lineKey, quantity) {
        const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: lineKey, quantity })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
    }

    // Dynamic mapping builder - parallel fetches, NO rate limiting
    // These are lightweight HTML template renders, not Shopify API calls
    async function buildMappingFromCart(cart) {
        const unknownItems = cart.items.filter(item =>
            PRODUCT_KEY_MAPPING[item.sku] === undefined &&
            !isFlaggedFree(item) &&
            item.handle && item.sku
        );

        if (unknownItems.length === 0) return;

        await Promise.all(unknownItems.map(async (item) => {
            try {
                const response = await fetch(`/products/${item.handle}?view=free-key-meta`);
                if (response.ok) {
                    const text = await response.text();
                    const keySku = text.trim();
                    PRODUCT_KEY_MAPPING[item.sku] = (keySku && keySku !== '') ? keySku : null;
                }
            } catch (e) {
                // Silent fail - will retry next cycle
            }
        }));
    }

    // Load the full product list once (paginated - /products.json caps at 250 per page)
    async function loadProductCache() {
        if (productCacheLoaded) return;
        if (productCachePromise) return productCachePromise;

        productCachePromise = (async () => {
            for (let page = 1; page <= PRODUCTS_MAX_PAGES; page++) {
                const data = await safeApiCall(`/products.json?limit=${PRODUCTS_PAGE_LIMIT}&page=${page}`);
                const products = data.products || [];
                for (const product of products) {
                    for (const variant of product.variants) {
                        if (variant.sku) {
                            keyProductCache[variant.sku] = { product, variant };
                        }
                    }
                }
                if (products.length < PRODUCTS_PAGE_LIMIT) break;
            }
            productCacheLoaded = true;
        })();

        try {
            await productCachePromise;
        } finally {
            productCachePromise = null;
        }
    }

    async function getProductBySku(sku) {
        if (keyProductCache[sku]) return keyProductCache[sku];
        await loadProductCache();
        return keyProductCache[sku] || null;
    }

    // Resolve a key SKU (35* or 53*) to its product's free + paid variants.
    // A "key product" = a product with one 53* variant and one 35* variant.
    // Keys not migrated yet fall back to the old behaviour (legacy: add the mapped variant itself, no 35* overflow).
    async function getKeyPair(sku) {
        if (!sku) return null;
        if (KEY_PAIRS[sku] !== undefined) return KEY_PAIRS[sku];

        const entry = await getProductBySku(sku);
        if (!entry) return null; // don't cache misses - cache may still be loading

        const variants = entry.product.variants || [];
        const free = variants.find(v => skuStarts(v.sku, FREE_PREFIX));
        const paid = variants.find(v => skuStarts(v.sku, PAID_PREFIX));

        if (free && paid) {
            const pair = { product: entry.product, free, paid, legacy: false };
            variants.forEach(v => { if (v.sku) KEY_PAIRS[v.sku] = pair; });
            return pair;
        }

        const legacyPair = { product: entry.product, free: entry.variant, paid: null, legacy: true };
        KEY_PAIRS[sku] = legacyPair;
        return legacyPair;
    }

    // ---------- UI ----------

    function ensurePopupStyles() {
        if (document.querySelector('style[data-popup-animations]')) return;
        const style = document.createElement('style');
        style.setAttribute('data-popup-animations', 'true');
        style.textContent = `
            @keyframes slideInRight {
                from { transform: translateX(100%); opacity: 0; }
                to { transform: translateX(0); opacity: 1; }
            }
            @keyframes slideOutRight {
                from { transform: translateX(0); opacity: 1; }
                to { transform: translateX(100%); opacity: 0; }
            }
            .free-key-popup-close {
                transition: all 0.2s ease;
            }
            .free-key-popup-close:hover {
                background: #f5f5f5 !important;
                transform: scale(1.1);
            }
            @media (max-width: 768px) {
                .free-key-popup {
                    top: 15px !important; right: 15px !important; left: 15px !important;
                    max-width: none !important; min-width: auto !important;
                }
            }
        `;
        document.head.appendChild(style);
    }

    function closePopup(popup) {
        popup.style.animation = 'slideOutRight 0.4s ease-in forwards';
        setTimeout(() => {
            if (popup && popup.parentElement) popup.remove();
        }, 400);
    }

    const CLOSE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--large" fill="none" viewBox="0 0 24 24">
      <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
      <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;

    const CLOSE_BTN_STYLE = `
        position: absolute; top: 12px; right: 12px; background: white;
        width: 35px; height: 35px; font-size: 20px; cursor: pointer;
        display: flex; align-items: center; justify-content: center;
        padding: 0; color: #000; line-height: 1;
    `;

    function mountPopup(popup, autoCloseMs) {
        document.body.appendChild(popup);

        const autoCloseTimeout = setTimeout(() => {
            if (popup && popup.parentElement) closePopup(popup);
        }, autoCloseMs);

        const closeBtn = popup.querySelector('.free-key-popup-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                clearTimeout(autoCloseTimeout);
                closePopup(popup);
            });
        }
    }

    function buildImageHtml(product) {
        const placeholder = `
            <div style="width: 100px; height: 100px; background: #f8f9fa; border: 3px solid #e9ecef; border-radius: 12px; margin-right: 25px; display: flex; align-items: center; justify-content: center; color: #6c757d; font-size: 12px; text-align: center; flex-shrink: 0;">
                No Image<br>Available
            </div>
        `;

        if (!product.images || product.images.length === 0) return placeholder;

        let imageUrl = product.images[0];
        if (typeof imageUrl === 'object' && imageUrl.src) {
            imageUrl = imageUrl.src;
        } else if (typeof imageUrl === 'object' && imageUrl.url) {
            imageUrl = imageUrl.url;
        }
        if (typeof imageUrl !== 'string') return placeholder;

        if (imageUrl.includes('?')) imageUrl = imageUrl.split('?')[0];
        imageUrl += '?v=' + Date.now() + '&width=200';

        return `
            <div style="width: 100px; height: 100px; margin-right: 25px; flex-shrink: 0;">
                <img src="${imageUrl}"
                     alt="${product.title || 'Product'}"
                     style="width: 100%; height: 100%; object-fit: cover; border-radius: 12px; border: 3px solid #f0f0f0; display: block; opacity: 0; transition: opacity 0.3s;"
                     onload="this.style.opacity='1'"
                     onerror="this.parentElement.innerHTML='<div style=\\'width:100%;height:100%;background:#f8f9fa;border:3px solid #e9ecef;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#6c757d;font-size:12px;text-align:center;\\'>No Image<br>Available</div>'">
            </div>
        `;
    }

    // Free key added popup (unchanged look)
    function showFreeKeyPopup(pair) {
        const existingPopup = document.querySelector('.free-key-popup');
        if (existingPopup) existingPopup.remove();
        ensurePopupStyles();

        const popup = document.createElement('div');
        popup.className = 'free-key-popup';
        popup.style.cssText = `
            position: fixed; top: 20px; right: 20px; background: #ffffff;
            border: 2px solid #e30613; border-radius: 16px; padding: 35px;
            box-shadow: 0 12px 40px rgba(0,0,0,0.25); z-index: 10000;
            max-width: 520px; min-width: 420px;
            animation: slideInRight 0.4s ease-out;
        `;

        popup.innerHTML = `
            <button class="free-key-popup-close" style="${CLOSE_BTN_STYLE}">${CLOSE_ICON}</button>
            <div style="display: flex; align-items: center; margin-bottom: 25px;">
                <div style="width: 32px; height: 32px; background: #e30613; border-radius: 50%; margin-right: 15px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 18px;">✔</div>
                <div style="font-weight: bold; color: #333; font-size: 22px;">Kostenloser Schlüssel hinzugefügt!</div>
            </div>
            <div style="display: flex; align-items: center;">
                ${buildImageHtml(pair.product)}
                <div style="flex: 1;">
                    <h4 style="margin: 0 0 10px 0; font-size: 18px; color: #333; font-weight: 600; line-height: 1.3;">${pair.product.title}</h4>
                    <p style="margin: 0; font-size: 15px; color: #666; line-height: 1.4; font-weight: 600;">Wir legen jedem Strahlregler einen kostenlosen Schlüssel bei. </p>
                    <p style="margin: 5px 0 0 0; font-size: 10px; color: #666; line-height: 1.4;">Um die Umwelt zu schonen, nutze am besten Werkzeuge, die du bereits zu Hause hast – so leisten wir gemeinsam einen Beitrag. Falls du den Schlüssel nicht benötigst, entferne ihn bitte im Warenkorb. Vielen Dank für deine Unterstützung!</p>
                </div>
            </div>
        `;

        mountPopup(popup, 6000);
    }

    // Shown when extra keys were moved onto the paid variant
    function showPaidKeyNotice(pair, extraQty) {
        const existingPopup = document.querySelector('.free-key-popup');
        if (existingPopup) existingPopup.remove();
        ensurePopupStyles();

        const popup = document.createElement('div');
        popup.className = 'free-key-popup';
        popup.style.cssText = `
            position: fixed; top: 20px; right: 20px; background: #ffffff;
            border: 2px solid #e30613; border-radius: 16px; padding: 25px 35px;
            box-shadow: 0 12px 40px rgba(0,0,0,0.25); z-index: 10000;
            max-width: 520px; min-width: 420px;
            animation: slideInRight 0.4s ease-out;
        `;

        popup.innerHTML = `
            <button class="free-key-popup-close" style="${CLOSE_BTN_STYLE}">${CLOSE_ICON}</button>
            <div style="font-weight: bold; color: #333; font-size: 18px; margin-bottom: 10px; padding-right: 30px;">
                ${extraQty === 1 ? 'Zusätzlicher Schlüssel hinzugefügt' : `${extraQty} zusätzliche Schlüssel hinzugefügt`}
            </div>
            <p style="margin: 0; font-size: 14px; color: #666; line-height: 1.4;">
                Ein Schlüssel ist kostenlos. Jeder weitere ${pair.product.title} wird zum regulären Preis berechnet.
            </p>
        `;

        mountPopup(popup, 6000);
    }

    // Cart refresh for both drawer and page
    async function refreshCart() {
        try {
            const isCartPage = window.location.pathname.includes('/cart');
            const cartDrawer = document.querySelector('cart-drawer');
            const mainCart = document.querySelector('main-cart');

            if (isCartPage && mainCart) {
                const freshCartData = await fetch('/cart.js').then(r => r.json());
                const isEmpty = freshCartData.item_count === 0;

                const cartWrapper = document.querySelector('.cart');
                const emptySection = document.querySelector('.cart__empty');

                if (isEmpty) {
                    if (mainCart) mainCart.style.display = 'none';
                    if (emptySection) emptySection.style.display = 'block';
                    if (cartWrapper) cartWrapper.classList.add('is-empty');
                } else {
                    if (emptySection) emptySection.style.display = 'none';
                    if (cartWrapper) cartWrapper.classList.remove('is-empty');
                    if (mainCart) mainCart.style.display = 'block';

                    const mainCartSection = mainCart.closest('.shopify-section');
                    const sectionId = mainCartSection ? mainCartSection.id.replace('shopify-section-', '') : 'template--24660145373511__main-cart';

                    const sectionResponse = await fetch(`${window.location.origin}/?section_id=${sectionId}`, {
                        headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
                    });

                    if (sectionResponse.ok) {
                        const sectionHTML = await sectionResponse.text();
                        const parser = new DOMParser();
                        const parsedHTML = parser.parseFromString(sectionHTML, 'text/html');
                        const newCartContent = parsedHTML.querySelector('main-cart');

                        if (newCartContent) {
                            mainCart.innerHTML = newCartContent.innerHTML;
                        }
                    }
                }
            } else if (cartDrawer && window.FoxTheme && window.FoxTheme.pubsub) {
                const sectionId = cartDrawer.getAttribute('data-section-id');
                if (sectionId) {
                    const cartJson = await fetch('/cart.js').then(r => r.json());
                    const sectionResponse = await fetch(`${window.location.origin}/?section_id=${sectionId}`, {
                        headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
                    });

                    if (sectionResponse.ok) {
                        const sectionHTML = await sectionResponse.text();
                        cartJson.sections = {};
                        cartJson.sections[sectionId] = sectionHTML;

                        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                            sources: 'free-key-system',
                            cart: cartJson
                        });
                    }
                }
            }
        } catch (error) {
            // Silent fail
        }
    }

    // ---------- Main processing (one cart change per cycle) ----------

    async function processCart() {
        if (isProcessing) return { success: false };
        isProcessing = true;

        try {
            const cart = await getCart();
            if (!cart || !cart.items) return { success: false };

            await buildMappingFromCart(cart);

            // Resolve key pairs only for SKUs that could be keys (avoids loading products.json for every cart)
            const skusToResolve = new Set();
            cart.items.forEach(item => {
                const mapped = PRODUCT_KEY_MAPPING[item.sku];
                if (mapped) skusToResolve.add(mapped);
                if (skuStarts(item.sku, FREE_PREFIX) || (isFlaggedFree(item) && item.sku)) {
                    skusToResolve.add(item.sku);
                }
            });
            for (const sku of skusToResolve) {
                await getKeyPair(sku);
            }

            const requiredFree = new Map();       // free SKU -> pair
            const freeLines = new Map();          // free SKU -> flagged line
            const strayFreeLines = [];            // free variant added without flag
            const legacyLines = [];               // flagged line on a non-free variant (old carts)
            const currentRequiredProducts = new Set();

            cart.items.forEach(item => {
                const flagged = isFlaggedFree(item);

                const mapped = PRODUCT_KEY_MAPPING[item.sku];
                if (mapped && !flagged) {
                    const reqPair = KEY_PAIRS[mapped];
                    if (reqPair) {
                        requiredFree.set(reqPair.free.sku, reqPair);
                        currentRequiredProducts.add(item.sku);
                    }
                }

                const pair = KEY_PAIRS[item.sku];
                if (!pair) {
                    if (flagged) legacyLines.push(item);
                    return;
                }

                if (item.sku === pair.free.sku) {
                    if (flagged) freeLines.set(item.sku, item);
                    else if (!pair.legacy) strayFreeLines.push({ item, pair }); // legacy key bought normally = fine
                } else if (flagged) {
                    legacyLines.push(item);
                }
            });

            const requiredProductsChanged = !areSetsEqual(currentRequiredProducts, lastRequiredProducts);
            if (requiredProductsChanged) {
                manuallyRemovedKeys.clear();
                lastRequiredProducts = new Set(currentRequiredProducts);
            }

            let changes = 0;

            // 1. Free-flagged line on a migrated key's 35* variant (cart from before the split) -> remove (step 4 re-adds as 53*)
            if (changes === 0 && legacyLines.length > 0) {
                await changeLine(legacyLines[0].key, 0);
                changes++;
            }

            // 2. 53* variant added without flag (direct add) -> convert to 35*
            if (changes === 0 && strayFreeLines.length > 0) {
                const { item, pair } = strayFreeLines[0];
                try {
                    await addToCart(pair.paid.id, item.quantity);
                } catch (e) {
                    console.warn(`Free key system: could not add paid key ${pair.paid.sku}`, e);
                }
                await changeLine(item.key, 0);
                changes++;
            }

            // 3. Free line qty > 1 -> keep 1 free, move the rest to 35*
            if (changes === 0) {
                for (const [freeSku, item] of freeLines) {
                    const pair = KEY_PAIRS[freeSku];
                    if (item.quantity > 1 && pair && !pair.legacy) {
                        const extraQty = item.quantity - 1;
                        let paidAdded = false;
                        try {
                            await addToCart(pair.paid.id, extraQty);
                            paidAdded = true;
                        } catch (e) {
                            console.warn(`Free key system: could not add paid key ${pair.paid.sku}`, e);
                        }
                        await changeLine(item.key, 1);
                        if (paidAdded) showPaidKeyNotice(pair, extraQty);
                        changes++;
                        break;
                    }
                }
            }

            // 4. Add missing free keys (skip manually removed ones)
            if (changes === 0) {
                for (const [freeSku, pair] of requiredFree) {
                    if (!freeLines.has(freeSku) && !manuallyRemovedKeys.has(freeSku) && pair.free.available) {
                        await addToCart(pair.free.id, 1, { [FREE_PROP]: 'true' });
                        showFreeKeyPopup(pair);
                        changes++;
                        break;
                    }
                }
            }

            // 5. Remove free keys no longer needed
            if (changes === 0) {
                for (const [freeSku, item] of freeLines) {
                    if (!requiredFree.has(freeSku)) {
                        await changeLine(item.key, 0);
                        changes++;
                        break;
                    }
                }
            }

            // 6. Detect manually removed keys
            if (changes === 0) {
                for (const freeSku of requiredFree.keys()) {
                    if (!freeLines.has(freeSku)) manuallyRemovedKeys.add(freeSku);
                }
            }

            if (changes > 0) {
                setTimeout(refreshCart, 300);
            }

            return { success: true, changes };

        } catch (error) {
            return { success: false, error: error.message };
        } finally {
            isProcessing = false;
        }
    }

    function areSetsEqual(setA, setB) {
        if (setA.size !== setB.size) return false;
        for (const item of setA) {
            if (!setB.has(item)) return false;
        }
        return true;
    }

    // ---------- Monitoring ----------

    let lastCartState = '';
    let lastCartItems = [];
    let cartChangeTimeout = null;

    async function monitorCart() {
        try {
            const cart = await fetch('/cart.js').then(r => r.json());
            const cartState = JSON.stringify(cart.items.map(i => ({ sku: i.sku, qty: i.quantity, f: isFlaggedFree(i) })));

            if (cartState !== lastCartState) {
                if (cartChangeTimeout) clearTimeout(cartChangeTimeout);

                detectManualKeyRemoval(lastCartItems, cart.items);

                cartChangeTimeout = setTimeout(async () => {
                    await processCart();
                    lastCartItems = [...cart.items];
                }, 500);

                lastCartState = cartState;
            }
        } catch (error) {
            // Silent fail
        }
    }

    // Detect when free keys are manually removed (tracked by free SKU)
    function detectManualKeyRemoval(previousItems, currentItems) {
        if (!previousItems || previousItems.length === 0) return;

        const previousFree = new Set(previousItems.filter(isFlaggedFree).map(i => i.sku));
        const currentFree = new Set(currentItems.filter(isFlaggedFree).map(i => i.sku));

        for (const sku of previousFree) {
            if (!currentFree.has(sku)) {
                manuallyRemovedKeys.add(sku);
                console.log(`Free key ${sku} manually removed - won't re-add automatically`);
            }
        }
    }

    // ---------- Public API ----------

    window.freeKeySystem = {
        check: processCart,
        viewCart: getCart,
        mapping: PRODUCT_KEY_MAPPING,
        keyPairs: KEY_PAIRS,
        status: 'v2 - 35*/53* variant mode',
        resetRemovedKeys: () => {
            manuallyRemovedKeys.clear();
            return 'Manually removed keys reset - free keys will be re-added if needed';
        },
        getRemovedKeys: () => Array.from(manuallyRemovedKeys),
        forceCheck: () => {
            if (cartChangeTimeout) clearTimeout(cartChangeTimeout);
            return processCart();
        }
    };

    function startMonitoring() {
        setInterval(monitorCart, 800);
        setTimeout(monitorCart, 300);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => setTimeout(startMonitoring, 200));
    } else {
        setTimeout(startMonitoring, 200);
    }

})();
